// assets/paket-groessen.js
import { Component } from '@theme/component';

/**
 * Paket-Größenauswahl.
 *
 * - Liest die Bestandteile (Label + Größen je Kategorie) und die Zuordnung
 *   Varianten-ID -> Kategorie aus zwei JSON-Scripts im Block.
 * - Befüllt pro Bestandteil ein <select> mit der Größenliste, die zur Kategorie
 *   (Kinder / Unisex / Damen) der aktuell gewählten Variante gehört.
 * - Reagiert auf Variantenwechsel über einen change-Listener am Produktformular
 *   UND einen MutationObserver auf input[name="id"] (Horizon-unabhängig).
 * - Beim Kategoriewechsel wird die bisherige Auswahl geleert.
 * - Blockiert "In den Warenkorb", solange nicht alle Größen gewählt sind.
 *
 * @extends Component
 */
class PaketGroessen extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.sectionId = this.dataset.sectionId;
    this.productForm = document.getElementById('BuyButtons-ProductForm-' + this.sectionId);

    this.parts = this.#readJson('[data-paket-parts]') || [];
    this.varMap = this.#readJson('[data-paket-varmap]') || {};
    this.selects = Array.from(this.querySelectorAll('.dt-paket__select'));
    this.notes = Array.from(this.querySelectorAll('.dt-paket__note'));
    this.variantInput =
      (this.productForm && this.productForm.querySelector('input[name="id"]')) ||
      document.querySelector('input[name="id"]');
    this.currentCategory = null;

    // Erstbefüllung anhand der aktuellen Variante.
    this.#applyCategory(this.#resolveCategory());

    // Auswahl in den Größen-Dropdowns (die Selects liegen im Component, nicht im Formular).
    this.addEventListener('change', this.#onSelectChange);

    // Variantenwechsel erkennen: change am Formular ...
    if (this.productForm) {
      this.productForm.addEventListener('change', this.#onFormChange);
      // ... "In den Warenkorb" sperren, solange nicht alles gewählt ist.
      this.productForm.addEventListener('click', this.#onAddClick, true);
    }
    // ... und robust per MutationObserver auf dem versteckten id-Feld.
    if (this.variantInput) {
      this.observer = new MutationObserver(this.#onVariantMutation);
      this.observer.observe(this.variantInput, { attributes: true, attributeFilter: ['value'] });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.removeEventListener('change', this.#onSelectChange);
    if (this.productForm) {
      this.productForm.removeEventListener('change', this.#onFormChange);
      this.productForm.removeEventListener('click', this.#onAddClick, true);
    }
    this.observer?.disconnect();
  }

  /** JSON aus einem <script type="application/json"> im Block lesen. */
  #readJson(selector) {
    const el = this.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      console.warn('[Paket-Größen] JSON konnte nicht gelesen werden:', selector, error);
      return null;
    }
  }

  /** Kategorie der aktuell gewählten Variante bestimmen (Fallback: erste bekannte). */
  #resolveCategory() {
    const id = this.variantInput?.value;
    if (id && this.varMap[id]) return this.varMap[id];
    const first = Object.values(this.varMap)[0];
    return first || 'Unisex';
  }

  #onFormChange = () => {
    const next = this.#resolveCategory();
    if (next !== this.currentCategory) {
      this.#applyCategory(next);
    } else if (this.#allChosen()) {
      this.#hideHint();
    }
  };

  #onVariantMutation = () => {
    const next = this.#resolveCategory();
    if (next !== this.currentCategory) this.#applyCategory(next);
  };

  /** Größe geändert: Lieferhinweise aktualisieren und ggf. Fehlerhinweis ausblenden. */
  #onSelectChange = (event) => {
    if (!event.target.classList.contains('dt-paket__select')) return;
    this.#updateNotes();
    if (this.#allChosen()) this.#hideHint();
  };

  /**
   * Alle Selects mit der Größenliste der Kategorie neu befüllen und die
   * bisherige Auswahl zurücksetzen.
   * @param {string} category
   */
  #applyCategory(category) {
    this.currentCategory = category;
    this.selects.forEach((select) => {
      const index = Number(select.dataset.partIndex);
      const part = this.parts[index];
      const sizes = this.#sizesFor(part, category);

      select.innerHTML = '';
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'Bitte wählen …';
      select.appendChild(placeholder);

      sizes.forEach((size) => {
        const option = document.createElement('option');
        option.value = size;
        option.textContent = size;
        select.appendChild(option);
      });

      select.value = '';
    });
    this.#hideHint();
    this.#updateNotes();
  }

  /**
   * Zeigt pro Artikel den hinterlegten Lieferhinweis für die gewählte Größe und
   * sammelt die aktiven Hinweise in der sichtbaren Bestell-Eigenschaft.
   */
  #updateNotes() {
    const messages = [];
    this.selects.forEach((select) => {
      const index = Number(select.dataset.partIndex);
      const part = this.parts[index];
      const size = select.value;
      const note = this.notes.find((n) => Number(n.dataset.noteFor) === index);
      const text = this.#hinweisFor(part, this.currentCategory, size);
      if (note) {
        const textEl = note.querySelector('.dt-paket__note-text');
        if (text) {
          if (textEl) textEl.textContent = text;
          note.hidden = false;
        } else {
          note.hidden = true;
        }
      }
      if (text) messages.push(text);
    });

    const input = this.refs?.hinweisInput;
    if (input) {
      if (messages.length) {
        input.disabled = false;
        input.value = messages.join(' · ');
      } else {
        input.disabled = true;
        input.value = '';
      }
    }
  }

  /** Lieferhinweis eines Bestandteils für die gewählte Kategorie + Größe (oder ''). */
  #hinweisFor(part, category, size) {
    if (!part || !part.hinweise || !size) return '';
    const key = this.#sizeKey(part, category);
    const group = part.hinweise[key] || part.hinweise[category];
    return group && group[size] ? group[size] : '';
  }

  /** Größenliste eines Bestandteils zur Kategorie (mit Alias, Fallback: erste Liste). */
  #sizesFor(part, category) {
    if (!part || !part.sizes) return [];
    const key = this.#sizeKey(part, category);
    return Array.isArray(part.sizes[key]) ? part.sizes[key] : [];
  }

  /**
   * Bestimmt den passenden Schlüssel in sizes. Die Variantenkategorie kann
   * "Erwachsene" heißen, während die Größen unter "Unisex" liegen.
   * @param {object} part
   * @param {string} category
   */
  #sizeKey(part, category) {
    if (Array.isArray(part.sizes[category])) return category;
    const alias = { Erwachsene: 'Unisex', Herren: 'Unisex', Adult: 'Unisex' };
    const mapped = alias[category];
    if (mapped && Array.isArray(part.sizes[mapped])) return mapped;
    return Object.keys(part.sizes)[0];
  }

  #allChosen() {
    return this.selects.every((select) => select.value.trim() !== '');
  }

  /** Klick auf "In den Warenkorb" abfangen, wenn nicht alle Größen gewählt sind. */
  #onAddClick = (event) => {
    const isAddToCart = event.target.closest('add-to-cart-component, .add-to-cart-button, [type="submit"]');
    if (!isAddToCart) return;
    if (this.#allChosen()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#showHint();
    const firstEmpty = this.selects.find((select) => !select.value.trim());
    firstEmpty?.focus();
  };

  #showHint() {
    if (this.refs?.hint) this.refs.hint.hidden = false;
    this.classList.add('has-error');
  }

  #hideHint() {
    if (this.refs?.hint) this.refs.hint.hidden = true;
    this.classList.remove('has-error');
  }
}

if (!customElements.get('paket-groessen-component')) {
  customElements.define('paket-groessen-component', PaketGroessen);
}
